import React, { useState, useMemo } from "react";

// ========== NMA-BANKRUPTCY: Creditor Section v1.0 ==========
// 5 Tools: Proof of Claim · Dividend Estimator · Meeting of Creditors · Creditor Rights · Priority of Claims
// © 2026 Dr. Olutoyese Oyelese

// ─── BIA s.136(1) Priority Order Data ───
const PRIORITY_ORDER = [
  { rank: 0, label: "Deemed Trusts (CPP/EI/Income Tax Deductions)", ref: "BIA s.67(2)-(3), ITA s.227(4)-(4.1)", desc: "Unremitted employee source deductions (CPP, EI, income tax withheld). These are NOT property of the bankrupt — they sit outside the estate entirely and must be paid first. Super-priority.", color: "#1a1a1a", bg: "#f5f5f5" },
  { rank: 0.5, label: "Super-Priority Wage Claims", ref: "BIA s.81.3", desc: "Employees owed wages from the 6 months before bankruptcy, up to $2,000 per employee. This is a charge on all current assets of the bankrupt. Paid before secured creditors from current assets.", color: "#4a2080", bg: "#f3eff9" },
  { rank: 0.6, label: "Super-Priority Pension Claims", ref: "BIA s.81.5–81.6", desc: "Unremitted employer and employee pension contributions. Creates a charge on all assets of the bankrupt. Paid before secured creditors.", color: "#4a2080", bg: "#f3eff9" },
  { rank: 1, label: "Secured Creditors", ref: "BIA s.69.3, s.127–132", desc: "Creditors with a mortgage, lien, or pledge against specific property. Paid from the proceeds of that specific collateral. If proceeds fall short, the shortfall becomes an unsecured claim.", color: "#1a5276", bg: "#eaf2f8" },
  { rank: 2, label: "(a) Funeral & Testamentary Expenses", ref: "BIA s.136(1)(a)", desc: "Reasonable funeral and estate expenses if the bankrupt is deceased.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 3, label: "(b) Administration Costs", ref: "BIA s.136(1)(b)", desc: "Costs of administering the bankruptcy: trustee's fees and disbursements, legal costs, and expenses of persons acting under court direction.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 4, label: "(c) OSB Superintendent's Levy", ref: "BIA s.136(1)(c), s.147, Rule 123", desc: "5% levy on all dividend payments to defray OSB supervision costs. Deducted proportionally from every payment the trustee makes to creditors.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 5, label: "(d) Employee Wage Claims", ref: "BIA s.136(1)(d)", desc: "Wages, salaries, commissions, and compensation owed to employees for the 6 months before bankruptcy, up to $2,000 per claimant. Also covers disbursements related to travelling expenses.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 6, label: "(d.1) Family Support Claims", ref: "BIA s.136(1)(d.1)", desc: "Periodic child support and spousal support arrears accrued in the year before bankruptcy, plus any lump sum payable. Must be provable under s.121(4).", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 7, label: "(e) Municipal Taxes", ref: "BIA s.136(1)(e)", desc: "Municipal taxes assessed within 2 years before bankruptcy, not exceeding the value of the bankrupt's interest in the taxed property (and only if not already a secured claim on that property).", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 8, label: "(f) Landlord Rent Claims", ref: "BIA s.136(1)(f)", desc: "Arrears of rent for 3 months before bankruptcy, plus accelerated rent for up to 3 months after (if lease allows). Capped at realization from property on the leased premises.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 9, label: "(g) First Execution Creditor Costs", ref: "BIA s.136(1)(g), s.70(2)", desc: "Fees and costs of the creditor who first filed an execution (e.g., garnishment or writ). Limited to realization from the property bound by that execution. Exempt from OSB levy.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 10, label: "(h)–(i) Employee Injury & Other Claims", ref: "BIA s.136(1)(h)–(i)", desc: "Claims for workers' compensation injuries within 12 months before bankruptcy; amounts employees are entitled to re: pension fund contributions.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 11, label: "(j) Crown Claims", ref: "BIA s.136(1)(j)", desc: "Claims by federal or provincial Crown not mentioned above (e.g., income tax arrears, HST/GST). Only applies to bankruptcies filed before the prescribed date.", color: "#6b4fa0", bg: "#f3eff9" },
  { rank: 12, label: "Ordinary Unsecured Creditors", ref: "BIA s.141", desc: "Credit cards, personal loans, lines of credit, medical bills, payday loans, trade debt. All ordinary unsecured creditors share remaining funds pro rata — each gets the same cents-on-the-dollar.", color: "#b44c3a", bg: "#fdf0ed" },
  { rank: 13, label: "Deferred Claims", ref: "BIA s.137", desc: "Claims by persons related to the bankrupt (e.g., loans from family members or related companies). Paid only after all other creditors are paid in full.", color: "#8a8480", bg: "#f7f6f3" },
];

// ─── BIA s.173(1) Opposition Grounds ───
const OPPOSITION_GROUNDS = [
  { para: "a", text: "Assets not equal to 50 cents on the dollar of unsecured liabilities", desc: "The bankrupt's property has not realized (or is unlikely to realize) at least 50¢ per dollar of unsecured debt, unless the bankrupt can show this is due to circumstances beyond their control.", ref: "s.173(1)(a), s.174" },
  { para: "b", text: "Continued to trade after becoming insolvent", desc: "The bankrupt continued carrying on business or trading after knowing they were insolvent.", ref: "s.173(1)(b)" },
  { para: "c", text: "Failed to account for losses or deficiency of assets", desc: "Cannot satisfactorily explain what happened to assets or why liabilities exceeded assets.", ref: "s.173(1)(c)" },
  { para: "d", text: "Bankruptcy caused by unjustifiable extravagance", desc: "Brought on or contributed to bankruptcy through rash or hazardous speculations, unjustifiable extravagance in living, gambling, or culpable neglect of business affairs.", ref: "s.173(1)(d)" },
  { para: "e", text: "Put creditors to unnecessary expense", desc: "Put creditors to unnecessary expense by a frivolous or vexatious defence to any action properly brought against the bankrupt.", ref: "s.173(1)(e)" },
  { para: "f", text: "Incurred unjustifiable debts within 3 months before bankruptcy", desc: "Within 3 months before the date of the initial bankruptcy event, incurred unjustifiable debts or liabilities.", ref: "s.173(1)(f)" },
  { para: "g", text: "Failed to account for loss of assets", desc: "Failed to account satisfactorily for any loss of assets or deficiency of assets to meet liabilities.", ref: "s.173(1)(g)" },
  { para: "h", text: "Obtained credit through misrepresentation", desc: "Within 12 months before the initial bankruptcy event, obtained credit through a false representation or fraud.", ref: "s.173(1)(h)" },
  { para: "i", text: "Failed to keep adequate books of account", desc: "Failed to keep books of account that are usual and proper in the business carried on by the bankrupt and that sufficiently disclose the business transactions and financial position during the 3-year period preceding the bankruptcy.", ref: "s.173(1)(i)" },
  { para: "j", text: "Concealed or destroyed books of account", desc: "Concealed, destroyed, mutilated, falsified, or failed to preserve any books or documents affecting the bankrupt's property or affairs.", ref: "s.173(1)(j)" },
  { para: "k", text: "Convicted of fraud or fraudulent breach of trust", desc: "Has been convicted of any offence under the BIA or has been guilty of fraud or fraudulent breach of trust.", ref: "s.173(1)(k)" },
  { para: "l", text: "Committed fraud or fraudulent breach of trust", desc: "Has committed any fraud or fraudulent breach of trust.", ref: "s.173(1)(l)" },
  { para: "m", text: "Surplus income obligations remain outstanding", desc: "The bankrupt has not paid the required surplus income payments. This ground goes to mediation before court (s.170.1).", ref: "s.173(1)(m)", mediation: true },
  { para: "n", text: "Could have made a viable proposal instead", desc: "The bankrupt could have made a viable proposal to creditors but chose bankruptcy instead as the means to resolve indebtedness. Goes to mediation first (s.170.1).", ref: "s.173(1)(n)", mediation: true },
  { para: "o", text: "Failed to perform duties under the BIA", desc: "Failed to perform the duties imposed by the BIA (s.158 duties) or to comply with any order of the court.", ref: "s.173(1)(o)" },
];

// ─── Creditor Rights Data ───
const CREDITOR_RIGHTS = [
  { title: "File a Proof of Claim", icon: "📋", desc: "Every creditor has the right to file a proof of claim (Form 31) with the trustee. This is how you establish your claim against the estate. Without a proved claim, you cannot vote at meetings or receive dividends.", ref: "BIA s.124(2), s.135" },
  { title: "Vote at Meetings of Creditors", icon: "🗳️", desc: "Once your claim is proved, you can vote at the first meeting and any subsequent meetings. You can affirm or replace the trustee, appoint inspectors, and give directions for estate administration.", ref: "BIA s.102(5), s.108" },
  { title: "Appoint or Serve as Inspector", icon: "🔍", desc: "Creditors may appoint up to 5 inspectors to oversee the trustee. Inspectors approve asset sales, review financial statements, and ensure the estate is properly administered.", ref: "BIA s.116–120" },
  { title: "Inspect Claims Filed by Others", icon: "👁️", desc: "You have the right to inspect any proof of claim filed by other creditors. If you believe a claim is invalid or inflated, you can raise this with the trustee, who can disallow it.", ref: "BIA s.135" },
  { title: "Examine the Bankrupt Under Oath", icon: "⚖️", desc: "Creditors may examine the bankrupt under oath about their assets, conduct, and causes of bankruptcy. This is done at the meeting of creditors or at a court-ordered examination.", ref: "BIA s.159–163" },
  { title: "Oppose the Bankrupt's Discharge", icon: "🚫", desc: "You can oppose the discharge if any of the 15 grounds under s.173(1) apply (fraud, non-cooperation, extravagance, etc.). You must file a notice of opposition before the automatic discharge date and present evidence to the court.", ref: "BIA s.168.2, s.172–173" },
  { title: "Take Over Trustee's Rights (s.38)", icon: "📝", desc: "If the trustee refuses to pursue a proceeding that would benefit the estate (e.g., recovering fraudulent transfers), you can apply to court for an order to take over that action in your own name, at your own expense and risk.", ref: "BIA s.38" },
  { title: "Report Suspected Fraud to OSB", icon: "🚨", desc: "If you suspect the bankrupt is hiding assets, making false declarations, or committing other offences, you can report this to the trustee or directly to the OSB's Special Investigation Units.", ref: "BIA s.198–208" },
  { title: "Request Surplus Income Updates", icon: "💰", desc: "By checking the appropriate box on Form 31, you can request to be informed whenever the trustee reviews and redetermines the bankrupt's surplus income obligations.", ref: "BIA s.68(4)" },
  { title: "Receive the Trustee's Discharge Report", icon: "📄", desc: "By checking the appropriate box on Form 31, you can request a copy of the trustee's report on the bankrupt's application for discharge (s.170 report).", ref: "BIA s.170(1)" },
];

// ─── Form 31 Fields Guide ───
const FORM31_SECTIONS = [
  { title: "1. Creditor Information", fields: "Full legal name of creditor (individual or company), address, phone, fax, email. If representative is filing, state position/title.", tip: "Use the exact legal name. A mailing address is required in all cases." },
  { title: "2. Debtor Information", fields: "Name and location of the debtor (bankrupt). Date of bankruptcy (or proposal/NOI date).", tip: "This information is provided in the notice from the trustee." },
  { title: "3. Amount of Claim", fields: "Total amount the debtor owes you as of the date of bankruptcy, after deducting counterclaims. Attach a detailed statement of account (Schedule A).", tip: "Schedule A must show dates, invoice numbers, and amounts — not just a balance forward. Convert non-Canadian currency at the bankruptcy date exchange rate." },
  { title: "4. Claim Classification", fields: "Check the type(s) that apply: (A) unsecured, (B) landlord claim, (C) secured, (D) farmer/fisherman/aquaculturist inventory, (E) wage earner, (F) pension claim, (G) claim against directors, (H) customer of securities firm.", tip: "You can claim under multiple categories. If claiming priority under s.136, specify the amount and section." },
  { title: "5. Secured Claim Details", fields: "If secured: describe the security, its date, and value. State whether you wish to be treated as a secured or unsecured creditor.", tip: "The trustee may require you to value your security. If proceeds from your collateral are insufficient, the shortfall becomes an unsecured claim." },
  { title: "6. Related Party Declaration", fields: "Declare whether you are related to the debtor within the meaning of BIA s.4.", tip: "Related parties face a 12-month lookback (instead of 3 months) for payments and transfers at undervalue, and their claims may be deferred under s.137." },
  { title: "7. Statute-Barred Debt", fields: "(New in 2024) Declare whether the debt has ever been statute-barred, whether limitations were extended, and any relevant details.", tip: "A statute-barred debt is NOT a provable claim. Provincial limitation periods vary (typically 2–6 years). The Income Tax Act has its own limitation periods." },
  { title: "8. Payments & Transfers", fields: "Disclose all payments received from the debtor, credits allowed, and transfers at undervalue within 3 months before the initial bankruptcy event (12 months if related party).", tip: "Be thorough — undisclosed preferences or transfers at undervalue can lead to your claim being disallowed." },
  { title: "9. Requests & Signature", fields: "Check boxes to: (a) be informed of surplus income changes, (b) receive the trustee's s.170 discharge report. Sign and have witnessed (or attach an affidavit).", tip: "Always check both boxes to stay informed about the estate. An affidavit, if attached, must be sworn before an authorized person." },
];

// ─── Colors (blue/navy scheme for Creditor section) ───
const C = {
  bg: "#f7f6f3",
  card: "#ffffff",
  text: "#2d2a26",
  textMid: "#55504a",
  textLight: "#8a8480",
  accent: "#1a5276",
  accentLight: "#eaf2f8",
  accentDark: "#0e3a57",
  warm: "#c4956a",
  warmLight: "#fdf5ed",
  warmDark: "#8b6340",
  border: "#e8e4df",
  danger: "#b44c3a",
  dangerLight: "#fdf0ed",
  warning: "#a07800",
  warningLight: "#fff9e6",
  info: "#3a6fb4",
  infoLight: "#edf4fd",
  purple: "#6b4fa0",
  purpleLight: "#f3eff9",
};

const TABS = [
  { id: "claim", label: "Proof of Claim", emoji: "📋" },
  { id: "dividend", label: "Dividend Estimator", emoji: "💰" },
  { id: "meeting", label: "Meetings", emoji: "🏛️" },
  { id: "rights", label: "Your Rights", emoji: "🛡️" },
  { id: "priority", label: "Priority of Claims", emoji: "📊" },
];

// ────────────────────────────────────────────
// TOOL 1: Proof of Claim Guide
// ────────────────────────────────────────────
function ProofOfClaimGuide() {
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Filing a Proof of Claim (Form 31)</h2>
        <p style={descS}>
          A proof of claim is how you tell the trustee: <em>"I'm owed money."</em> Without one, you can't vote at creditor meetings or receive dividends. The current Form 31 came into effect September 16, 2024, under Directive 8R22.
        </p>
        <div style={{ background: C.warningLight, border: `1px solid #e8d38a`, borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
          <strong style={{ color: C.warning }}>⏰ Key Deadlines</strong>
          <div style={{ marginTop: 8, color: C.textMid }}>
            <strong>To vote at the first meeting:</strong> File before the meeting starts (meeting is within 21 days of trustee appointment).
            <br /><strong>To receive dividends:</strong> File before the trustee sends the final dividend notice. If you miss this notice, you have 30 days — after that, you're barred from any dividend.
          </div>
        </div>
      </div>

      <h3 style={{ fontSize: 17, fontWeight: 700, margin: "24px 0 12px", color: C.text }}>Form 31 — Section by Section</h3>
      {FORM31_SECTIONS.map((s, i) => (
        <div key={i} style={{ ...cardS, padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={() => setExpanded(expanded === i ? null : i)}>
          <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{s.title}</span>
            <span style={{ fontSize: 18, color: C.textLight, transform: expanded === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
          </div>
          {expanded === i && (
            <div style={{ padding: "0 20px 16px", borderTop: `1px solid ${C.border}` }}>
              <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, margin: "12px 0 8px" }}>{s.fields}</p>
              <div style={{ background: C.accentLight, borderRadius: 6, padding: 12, fontSize: 13, color: C.accent, lineHeight: 1.5 }}>
                <strong>💡 Tip:</strong> {s.tip}
              </div>
            </div>
          )}
        </div>
      ))}

      <div style={{ ...cardS, marginTop: 24 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 12px", color: C.text }}>Types of Claims</h3>
        <div style={{ display: "grid", gap: 12 }}>
          {[
            { type: "Unsecured", desc: "Credit cards, personal loans, trade debt, medical bills. No collateral. Paid pro rata after preferred creditors.", color: C.info, bg: C.infoLight },
            { type: "Secured", desc: "Mortgage, vehicle lien, equipment lease. Paid from collateral. Shortfall becomes unsecured.", color: C.purple, bg: C.purpleLight },
            { type: "Preferred (s.136)", desc: "Employee wages, family support, municipal taxes, landlord rent — paid in priority order before ordinary unsecured.", color: C.accent, bg: C.accentLight },
            { type: "Farmer/Fisher/Aquaculturist", desc: "Inventory supplied within 15 days before bankruptcy. Creates a priority lien on all debtor inventory. Must file within 30 days (s.81.2).", color: C.warm, bg: C.warmLight },
          ].map((c, i) => (
            <div key={i} style={{ background: c.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
              <strong style={{ color: c.color, fontSize: 14 }}>{c.type}</strong>
              <p style={{ fontSize: 13, color: C.textMid, margin: "4px 0 0", lineHeight: 1.5 }}>{c.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardS, marginTop: 12, background: C.dangerLight, border: `1px solid #e8b0a4` }}>
        <strong style={{ color: C.danger, fontSize: 14 }}>⚠️ Penalties for False Claims</strong>
        <p style={{ fontSize: 13, color: C.textMid, margin: "8px 0 0", lineHeight: 1.5 }}>
          The BIA imposes strict penalties for making false claims, proofs, declarations, or statements of account. The chair of the creditors' meeting and the trustee can disallow inaccurate claims. Filing a false claim is an offence under BIA s.200–201.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// TOOL 2: Dividend Estimator
// ────────────────────────────────────────────
function DividendEstimator() {
  const [inputs, setInputs] = useState({
    totalAssets: "",
    securedClaims: "",
    preferredClaims: "",
    trusteeFees: "",
    totalUnsecured: "",
    yourClaim: "",
    isSummary: false,
  });

  const p = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
  const fmt = v => `$${v.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const calc = useMemo(() => {
    const assets = p(inputs.totalAssets);
    const secured = p(inputs.securedClaims);
    const preferred = p(inputs.preferredClaims);
    const fees = p(inputs.trusteeFees);
    const unsecured = p(inputs.totalUnsecured);
    const yours = p(inputs.yourClaim);

    const afterSecured = Math.max(0, assets - secured);
    const afterFees = Math.max(0, afterSecured - fees);
    const levyRate = 0.05; // 5% for most estates
    const levy = afterFees * levyRate;
    const afterLevy = Math.max(0, afterFees - levy);
    const afterPreferred = Math.max(0, afterLevy - preferred);
    const centsOnDollar = unsecured > 0 ? Math.min(1, afterPreferred / unsecured) : 0;
    const yourDividend = yours * centsOnDollar;
    const yourAfterLevy = yourDividend * (1 - levyRate);

    return { assets, secured, afterSecured, fees, afterFees, levy, afterLevy, preferred, afterPreferred, unsecured, centsOnDollar, yours, yourDividend, yourAfterLevy };
  }, [inputs]);

  const set = (k, v) => setInputs(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Dividend Estimator</h2>
        <p style={descS}>
          Estimate how much you might recover from a bankruptcy estate. In most consumer bankruptcies, unsecured creditors receive little or nothing. This tool shows why.
        </p>
      </div>

      <div style={cardS}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 16px", color: C.text }}>Estate Information</h3>
        <div style={rowS}>
          <div>
            <label style={labelS}>Total Estate Assets (realizable value)</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.totalAssets} onChange={e => set("totalAssets", e.target.value)} />
          </div>
          <div>
            <label style={labelS}>Secured Claims (mortgages, liens)</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.securedClaims} onChange={e => set("securedClaims", e.target.value)} />
          </div>
        </div>
        <div style={rowS}>
          <div>
            <label style={labelS}>Trustee Fees & Legal Costs</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.trusteeFees} onChange={e => set("trusteeFees", e.target.value)} />
          </div>
          <div>
            <label style={labelS}>Total Preferred Claims (s.136)</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.preferredClaims} onChange={e => set("preferredClaims", e.target.value)} />
          </div>
        </div>
        <div style={rowS}>
          <div>
            <label style={labelS}>Total Unsecured Claims (all creditors)</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.totalUnsecured} onChange={e => set("totalUnsecured", e.target.value)} />
          </div>
          <div>
            <label style={labelS}>Your Unsecured Claim</label>
            <input style={inputS} type="number" placeholder="0.00" value={inputs.yourClaim} onChange={e => set("yourClaim", e.target.value)} />
          </div>
        </div>
      </div>

      {calc.assets > 0 && (
        <div style={cardS}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 16px", color: C.text }}>Distribution Waterfall</h3>
          {[
            { label: "Total Estate Assets", value: calc.assets, sub: false },
            { label: "Less: Secured Claims", value: -calc.secured, sub: true },
            { label: "Available After Secured", value: calc.afterSecured, sub: false },
            { label: "Less: Trustee Fees & Legal", value: -calc.fees, sub: true },
            { label: "Available After Admin", value: calc.afterFees, sub: false },
            { label: "Less: OSB Levy (5%)", value: -calc.levy, sub: true },
            { label: "Available After Levy", value: calc.afterLevy, sub: false },
            { label: "Less: Preferred Claims (s.136)", value: -calc.preferred, sub: true },
            { label: "Available for Unsecured Creditors", value: calc.afterPreferred, sub: false, highlight: true },
          ].map((row, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 6, marginBottom: 2, background: row.highlight ? C.accentLight : row.sub ? "#fafaf8" : "transparent", fontWeight: row.highlight ? 700 : row.sub ? 400 : 600, fontSize: row.sub ? 13 : 14, color: row.value < 0 ? C.danger : C.text }}>
              <span style={{ paddingLeft: row.sub ? 16 : 0 }}>{row.label}</span>
              <span>{fmt(Math.abs(row.value))}{row.value < 0 ? " −" : ""}</span>
            </div>
          ))}

          <div style={{ marginTop: 20, background: calc.centsOnDollar > 0 ? C.accentLight : C.dangerLight, border: `1px solid ${calc.centsOnDollar > 0 ? "#b0d4f1" : "#e8b0a4"}`, borderRadius: 10, padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textMid, marginBottom: 4 }}>Recovery Rate for Unsecured Creditors</div>
            <div style={{ fontSize: 36, fontWeight: 700, color: calc.centsOnDollar > 0 ? C.accent : C.danger }}>
              {(calc.centsOnDollar * 100).toFixed(1)}¢
              <span style={{ fontSize: 18, fontWeight: 400 }}> on the dollar</span>
            </div>
            {calc.yours > 0 && (
              <div style={{ marginTop: 12, fontSize: 15, color: C.textMid }}>
                Your estimated dividend: <strong style={{ color: C.accent }}>{fmt(calc.yourAfterLevy)}</strong>
                <span style={{ fontSize: 12, marginLeft: 4 }}>(after 5% levy)</span>
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, background: C.warningLight, border: `1px solid #e8d38a`, borderRadius: 8, padding: 14, fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>
            <strong style={{ color: C.warning }}>Note:</strong> This is a simplified estimate. Actual dividends depend on asset realization, disputed claims, ongoing administration costs, and interim distributions. In most summary-administration consumer bankruptcies (assets under $15,000), unsecured creditors receive no dividend.
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// TOOL 3: Meeting of Creditors Guide
// ────────────────────────────────────────────
function MeetingOfCreditors() {
  const [expanded, setExpanded] = useState(null);

  const meetings = [
    {
      title: "First Meeting of Creditors",
      ref: "BIA s.102",
      when: "Within 21 days of the trustee's appointment (extendable by the Official Receiver).",
      chaired: "Official Receiver (or nominee).",
      notice: "Trustee sends notice to all known creditors within 5 days of appointment, along with: list of creditors ($25+), proof of claim form, proxy form, and (for individuals) financial situation information and surplus income obligations.",
      purpose: [
        "Consider the affairs of the bankrupt",
        "Affirm the trustee or appoint a replacement",
        "Appoint up to 5 inspectors",
        "Give directions to the trustee regarding estate administration",
      ],
      vote: "You must have a proved claim to vote. File your proof of claim before the meeting starts.",
      tips: "You can attend in person, by phone/video, or send a proxy. If you want to replace the trustee, coordinate with other creditors beforehand — you'll need a majority vote."
    },
    {
      title: "Subsequent Meetings",
      ref: "BIA s.103–104",
      when: "Called by the trustee at any time, or when directed by the court, or when requested by a majority of inspectors, or by 25% in number of creditors holding 25% in value of proved claims.",
      chaired: "Trustee or as directed by inspectors.",
      notice: "At least 5 days' notice with agenda. Only creditors who have proved their claims receive notice.",
      purpose: [
        "Review trustee's administration",
        "Approve asset sales or settlements",
        "Discuss surplus income determinations",
        "Any other estate business",
      ],
      vote: "Same rules — proved claim required.",
      tips: "If you believe the trustee is not acting in the creditors' best interest, requesting a meeting is a powerful tool. The 25%/25% threshold is calculated on proved claims only."
    },
    {
      title: "Meeting to Consider Discharge",
      ref: "BIA s.170, 172",
      when: "If the bankrupt's discharge is opposed, the trustee applies to court. The s.170 report is prepared and filed.",
      chaired: "Court (not a creditor meeting per se — it's a court hearing).",
      notice: "Trustee provides s.170 report to all creditors who requested it.",
      purpose: [
        "Court reviews trustee's report on the bankrupt's conduct",
        "Opposing creditors present evidence of s.173 grounds",
        "Court decides: absolute, conditional, suspended, or refused discharge",
      ],
      vote: "This is a court hearing, not a vote. You present evidence to support your opposition.",
      tips: "If you intend to oppose discharge, file your notice of opposition with the trustee, bankrupt, and OSB before the automatic discharge date. You must be prepared to prove your allegations."
    },
  ];

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Meetings of Creditors</h2>
        <p style={descS}>
          As a creditor, meetings are your primary opportunity to have a say in how the bankruptcy is administered. Most creditor meetings are brief — but understanding when and how to use them gives you leverage.
        </p>
      </div>

      {meetings.map((m, i) => (
        <div key={i} style={{ ...cardS, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => setExpanded(expanded === i ? null : i)}>
            <div>
              <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{m.title}</span>
              <span style={{ fontSize: 12, color: C.accent, marginLeft: 8, background: C.accentLight, padding: "2px 8px", borderRadius: 4 }}>{m.ref}</span>
            </div>
            <span style={{ fontSize: 18, color: C.textLight, transform: expanded === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
          </div>
          {expanded === i && (
            <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${C.border}` }}>
              {[
                { label: "When", value: m.when },
                { label: "Chaired By", value: m.chaired },
                { label: "Notice", value: m.notice },
                { label: "Who Can Vote", value: m.vote },
              ].map((field, j) => (
                <div key={j} style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: C.textLight, marginBottom: 4 }}>{field.label}</div>
                  <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5 }}>{field.value}</div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: C.textLight, marginBottom: 4 }}>Purpose</div>
                {m.purpose.map((p, k) => (
                  <div key={k} style={{ fontSize: 14, color: C.textMid, padding: "4px 0 4px 16px", position: "relative", lineHeight: 1.5 }}>
                    <span style={{ position: "absolute", left: 0, color: C.accent }}>•</span> {p}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, background: C.accentLight, borderRadius: 6, padding: 12, fontSize: 13, color: C.accent, lineHeight: 1.5 }}>
                <strong>💡 Practical Tip:</strong> {m.tips}
              </div>
            </div>
          )}
        </div>
      ))}

      <div style={cardS}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: C.text }}>Inspectors</h3>
        <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, margin: "0 0 12px" }}>
          Inspectors are creditors (or their representatives) appointed at the first meeting to oversee the trustee. They're especially important in commercial bankruptcies with significant assets.
        </p>
        <div style={{ display: "grid", gap: 8 }}>
          {[
            "Approve or give directions on asset sales",
            "Review and approve the trustee's statement of receipts and disbursements",
            "Authorize legal proceedings by the trustee",
            "Approve the trustee's fees (in ordinary administrations)",
            "Provide direction on surplus income disputes",
          ].map((duty, i) => (
            <div key={i} style={{ fontSize: 13, color: C.textMid, padding: "6px 12px", background: "#fafaf8", borderRadius: 6, lineHeight: 1.5 }}>
              <span style={{ color: C.accent, fontWeight: 700, marginRight: 8 }}>→</span>{duty}
            </div>
          ))}
        </div>
      </div>

      <div style={cardS}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: C.text }}>Proxies & Voting</h3>
        <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, margin: 0 }}>
          If you can't attend a meeting in person, you may appoint a proxy holder using the proxy form included with the notice of bankruptcy. The proxy must be signed and can authorize your representative to vote on specific resolutions or give them general voting authority. No name should be pre-inserted in the proxy form before it's sent to creditors — this is a BIA requirement to prevent trustees from steering votes.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// TOOL 4: Creditor Rights + Opposition to Discharge
// ────────────────────────────────────────────
function CreditorRightsAndOpposition() {
  const [expandedRight, setExpandedRight] = useState(null);
  const [expandedGround, setExpandedGround] = useState(null);
  const [section, setSection] = useState("rights");

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Creditor Rights & Opposition to Discharge</h2>
        <p style={descS}>
          The BIA gives creditors meaningful tools. Most creditors don't use them. Understanding your rights can make the difference between getting nothing and getting a meaningful recovery — or at least accountability.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { id: "rights", label: "Your Rights" },
            { id: "opposition", label: "Opposition to Discharge (s.173)" },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSection(tab.id)} style={{
              padding: "10px 16px", fontSize: 13, fontWeight: section === tab.id ? 700 : 500,
              background: section === tab.id ? C.accent : "transparent",
              color: section === tab.id ? "white" : C.textMid,
              border: `1px solid ${section === tab.id ? C.accent : C.border}`,
              borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {section === "rights" && (
        <div style={{ display: "grid", gap: 12 }}>
          {CREDITOR_RIGHTS.map((r, i) => (
            <div key={i} style={{ ...cardS, marginBottom: 0, padding: 0, overflow: "hidden", cursor: "pointer" }} onClick={() => setExpandedRight(expandedRight === i ? null : i)}>
              <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 22 }}>{r.icon}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{r.title}</span>
                  <span style={{ fontSize: 11, color: C.accent, marginLeft: 8 }}>{r.ref}</span>
                </div>
                <span style={{ fontSize: 18, color: C.textLight, transform: expandedRight === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </div>
              {expandedRight === i && (
                <div style={{ padding: "0 20px 16px", borderTop: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, margin: "12px 0 0" }}>{r.desc}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {section === "opposition" && (
        <div>
          <div style={cardS}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: C.text }}>How Opposition Works</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                { step: "1", title: "File Notice of Opposition", desc: "Before the automatic discharge date, give written notice to the trustee, the bankrupt, and the OSB stating your grounds for opposition." },
                { step: "2", title: "Automatic Discharge Is Blocked", desc: "If opposition is filed, the bankrupt does not get automatically discharged. The trustee must apply to court for a hearing." },
                { step: "3", title: "Present Evidence", desc: "At the hearing, you must prove the facts supporting your opposition. It's not enough to allege — you must provide evidence (documents, affidavits, witness testimony)." },
                { step: "4", title: "Court Decides", desc: "The court may: (a) grant an absolute discharge, (b) grant a conditional discharge (e.g., payment terms), (c) suspend the discharge, or (d) refuse the discharge." },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 14, padding: "12px 14px", background: "#fafaf8", borderRadius: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.accent, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{s.step}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5, marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cardS, background: C.warningLight, border: `1px solid #e8d38a` }}>
            <strong style={{ color: C.warning, fontSize: 14 }}>⚡ Mediation First for Grounds (m) and (n)</strong>
            <p style={{ fontSize: 13, color: C.textMid, margin: "8px 0 0", lineHeight: 1.5 }}>
              If the only grounds for opposition are surplus income obligations (m) or that the bankrupt could have made a viable proposal (n), the matter goes to OSB mediation before court. The trustee sends the application for mediation within 5 days of when the automatic discharge would have taken effect. If mediation fails, it proceeds to court.
            </p>
          </div>

          <h3 style={{ fontSize: 17, fontWeight: 700, margin: "24px 0 12px", color: C.text }}>15 Grounds for Opposition — BIA s.173(1)</h3>
          {OPPOSITION_GROUNDS.map((g, i) => (
            <div key={i} style={{ ...cardS, padding: 0, overflow: "hidden", marginBottom: 8, cursor: "pointer" }} onClick={() => setExpandedGround(expandedGround === i ? null : i)}>
              <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: C.accent, background: C.accentLight, padding: "2px 8px", borderRadius: 4, flexShrink: 0 }}>({g.para})</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: C.text, flex: 1 }}>{g.text}</span>
                {g.mediation && <span style={{ fontSize: 10, background: C.warningLight, color: C.warning, padding: "2px 6px", borderRadius: 4, fontWeight: 600, flexShrink: 0 }}>MEDIATION</span>}
                <span style={{ fontSize: 16, color: C.textLight, transform: expandedGround === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </div>
              {expandedGround === i && (
                <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5, margin: "10px 0 4px" }}>{g.desc}</p>
                  <span style={{ fontSize: 11, color: C.textLight }}>{g.ref}</span>
                </div>
              )}
            </div>
          ))}

          <div style={cardS}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: C.text }}>Discharge Outcomes</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                { type: "Absolute Discharge", desc: "Bankrupt is released from all debts (except s.178 surviving debts). Fresh start.", color: "#1a6847", bg: "#e8f5ee" },
                { type: "Conditional Discharge", desc: "Bankrupt must meet conditions (usually pay a specified amount over time). Once conditions are met, discharge becomes absolute.", color: C.accent, bg: C.accentLight },
                { type: "Suspended Discharge", desc: "Absolute discharge will take effect at a later date specified by the court.", color: C.warning, bg: C.warningLight },
                { type: "Refused Discharge", desc: "Bankrupt remains bankrupt. May apply again later. Most serious outcome — typically reserved for cases involving fraud or gross misconduct.", color: C.danger, bg: C.dangerLight },
              ].map((o, i) => (
                <div key={i} style={{ background: o.bg, borderRadius: 8, padding: 14, border: `1px solid ${C.border}` }}>
                  <strong style={{ color: o.color, fontSize: 14 }}>{o.type}</strong>
                  <p style={{ fontSize: 13, color: C.textMid, margin: "4px 0 0", lineHeight: 1.5 }}>{o.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cardS, background: C.infoLight, border: `1px solid #b0d4f1` }}>
            <strong style={{ color: C.info, fontSize: 14 }}>💡 s.178 Strategy</strong>
            <p style={{ fontSize: 13, color: C.textMid, margin: "8px 0 0", lineHeight: 1.5 }}>
              If your debt survives bankruptcy under s.178 (e.g., fraud, support obligations, student loans under 7 years), you may choose <em>not</em> to oppose the discharge. Instead, let the bankruptcy conclude and then pursue collection of the surviving debt post-discharge, without competing with other creditors. Consult a lawyer about this strategic choice.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// TOOL 5: Priority of Claims (s.136)
// ────────────────────────────────────────────
function PriorityOfClaims() {
  const [expanded, setExpanded] = useState(null);

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Priority of Claims — Who Gets Paid First</h2>
        <p style={descS}>
          Not all creditors are equal. BIA s.136 establishes a strict waterfall: each level is paid in full before the next level receives anything. Understanding where you stand in line is essential for setting realistic expectations.
        </p>
      </div>

      <div style={{ position: "relative" }}>
        {PRIORITY_ORDER.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 0, marginBottom: 4 }}>
            {/* Left: vertical line + rank indicator */}
            <div style={{ width: 48, display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: item.color, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                {item.rank <= 1 ? "★" : i < PRIORITY_ORDER.length - 1 ? i + 1 : "—"}
              </div>
              {i < PRIORITY_ORDER.length - 1 && (
                <div style={{ width: 2, flex: 1, background: "#e0ddd8", minHeight: 20 }} />
              )}
            </div>

            {/* Right: content card */}
            <div style={{ flex: 1, background: item.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 0, overflow: "hidden", cursor: "pointer", marginBottom: 4 }} onClick={() => setExpanded(expanded === i ? null : i)}>
              <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: item.color }}>{item.label}</span>
                </div>
                <span style={{ fontSize: 16, color: C.textLight, transform: expanded === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </div>
              {expanded === i && (
                <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, margin: "10px 0 6px" }}>{item.desc}</p>
                  <span style={{ fontSize: 11, color: C.textLight, fontFamily: "'IBM Plex Mono', monospace" }}>{item.ref}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...cardS, marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: C.text }}>Key Principles</h3>
        <div style={{ display: "grid", gap: 10 }}>
          {[
            { title: "Pari Passu", desc: "Within the same class, creditors share pro rata — each gets the same percentage of what they're owed. No one creditor jumps ahead of another in the same class." },
            { title: "Secured Creditors Are Separate", desc: "Secured creditors are paid from their specific collateral, not from the general pool. If the collateral is worth less than the debt, the shortfall becomes an unsecured claim." },
            { title: "Levy Applies to Everyone", desc: "The 5% OSB levy (s.147) is deducted from all payments — secured, preferred, and unsecured alike. It funds the OSB's supervision of the insolvency system." },
            { title: "Most Consumer Estates Have No Dividend", desc: "In summary administrations (assets under $15,000), after trustee fees and levy, there is often nothing left for unsecured creditors. This is the reality of most personal bankruptcies." },
            { title: "Related Party Claims Are Last", desc: "Loans from family members or related companies are deferred under s.137 and paid only after every other creditor has been paid in full." },
          ].map((p, i) => (
            <div key={i} style={{ background: "#fafaf8", borderRadius: 8, padding: 14 }}>
              <strong style={{ fontSize: 14, color: C.text }}>{p.title}</strong>
              <p style={{ fontSize: 13, color: C.textMid, margin: "4px 0 0", lineHeight: 1.5 }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardS, background: C.accentLight, border: `1px solid #b0d4f1` }}>
        <strong style={{ color: C.accent, fontSize: 14 }}>📌 Where Do You Stand?</strong>
        <p style={{ fontSize: 13, color: C.textMid, margin: "8px 0 0", lineHeight: 1.5 }}>
          If you're an ordinary unsecured creditor (credit card company, personal loan lender, supplier), you're near the bottom of the waterfall. Your best recovery tools are: (1) filing your proof of claim early, (2) serving as an inspector to monitor the estate, (3) opposing discharge if the bankrupt engaged in misconduct, and (4) pursuing s.178 claims post-discharge if applicable.
        </p>
      </div>
    </div>
  );
}

// ─── Shared styles ───
const cardS = { background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 28, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };
const titleS = { fontSize: 20, fontWeight: 700, margin: "0 0 8px", color: C.text };
const descS = { fontSize: 15, color: C.textLight, margin: "0 0 20px", lineHeight: 1.6 };
const labelS = { display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6, color: C.text };
const inputS = { width: "100%", padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", outline: "none" };
const rowS = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 };

// ────────────────────────────────────────────
// MAIN APP
// ────────────────────────────────────────────
export default function CreditorSection() {
  const [activeTab, setActiveTab] = useState("claim");
  const renderTab = () => {
    switch (activeTab) {
      case "claim": return <ProofOfClaimGuide />;
      case "dividend": return <DividendEstimator />;
      case "meeting": return <MeetingOfCreditors />;
      case "rights": return <CreditorRightsAndOpposition />;
      case "priority": return <PriorityOfClaims />;
      default: return <ProofOfClaimGuide />;
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: C.bg, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;600;700&display=swap" rel="stylesheet" />
      <header style={{ background: "linear-gradient(135deg, #1a5276 0%, #0e3a57 100%)", color: "white" }}>
        <div style={{ background: "#111", padding: "8px 24px", fontSize: 13, display: "flex", alignItems: "center", gap: 8, letterSpacing: "0.03em" }}>
          <span style={{ color: "#aaa" }}>navigationalmind.com</span>
          <span style={{ color: "#555" }}>/</span>
          <span style={{ color: "#aaa" }}>nma-bankruptcy</span>
          <span style={{ color: "#555" }}>/</span>
          <span style={{ color: "#ddd" }}>creditor</span>
          <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, background: "rgba(255,255,255,0.15)", fontSize: 11, fontWeight: 600, marginLeft: 12, letterSpacing: "0.04em" }}>v1.0</span>
        </div>
        <div style={{ padding: "48px 24px 32px", maxWidth: 900, margin: "0 auto" }}>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 700, margin: "0 0 12px", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            You're Owed Money.<br />Here's How to Recover It.
          </h1>
          <p style={{ fontSize: "clamp(15px, 2.5vw, 18px)", opacity: 0.85, margin: 0, lineHeight: 1.5, maxWidth: 650 }}>
            Tools to understand your rights as a creditor in a Canadian bankruptcy. File claims, estimate dividends, oppose discharge, and know where you stand in line.
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, padding: "0 24px", overflowX: "auto", maxWidth: 960, margin: "0 auto" }}>
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: "12px 16px", fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500,
              color: activeTab === tab.id ? "white" : "rgba(255,255,255,0.65)",
              background: activeTab === tab.id ? "rgba(255,255,255,0.15)" : "transparent",
              border: "none", borderBottom: activeTab === tab.id ? "3px solid #fff" : "3px solid transparent",
              cursor: "pointer", whiteSpace: "nowrap", borderRadius: "8px 8px 0 0", transition: "all 0.2s", fontFamily: "inherit",
            }}>
              <span style={{ marginRight: 6 }}>{tab.emoji}</span>{tab.label}
            </button>
          ))}
        </div>
      </header>
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 80px" }}>{renderTab()}</main>
      <footer style={{ textAlign: "center", padding: "32px 24px", fontSize: 13, color: C.textLight, borderTop: `1px solid ${C.border}`, maxWidth: 900, margin: "0 auto" }}>
        <p><strong>NMA Bankruptcy</strong> — Navigational Mind Architecture</p>
        <p style={{ marginTop: 8 }}>This tool provides general information based on the Bankruptcy & Insolvency Act. It is not legal advice. Consult a Licensed Insolvency Trustee or insolvency lawyer for advice specific to your circumstances.</p>
        <p style={{ marginTop: 8, fontSize: 12 }}>Priority of claims: BIA s.136(1) · Opposition to discharge: BIA s.173(1) · Proof of claim: Form 31 (Directive 8R22, Sept 2024) · OSB levy: BIA s.147, Rule 123</p>
      </footer>
    </div>
  );
}
